<template>
  <v-card class="mx-auto d-flex flex-column" flat height="100%" max-width="1280" width="100%" >
    <v-card-text class="pa-0">
      <!-- Status -->
      <StatusAutomatic
        v-if="sessionKpi.id && !isManualKpi"
        @toggleCronJob="toggleCronJob"
      />
      <StatusManual
        v-else-if="isManualKpi"
        :kpiType="sessionKpi.kpiType"
        :valueType="sessionKpi.definition?.valueType || 'QUANTITATIVE'"
      />
      <v-divider class="my-2"></v-divider>
      <!-- Value Badges -->
      <BadgesDetails
        :statistics="statistics"
        :targetValue="sessionKpi.definition?.targetValue !== undefined ? sessionKpi.definition.targetValue : 0"
        :isManualKpi="isManualKpi"
      />
      <!-- KPI Chart -->
      <v-row class="d-flex justify-center">
        <v-col cols="12">
          <ChartDetails
            :data="chartData"
            :type="chartType"
            :target-value="sessionKpi.definition.targetValue"
            :mockupAlert="mockupAlert"
            :isQualitative="isQualitative"
            :horizontal="chartHorizontal"
          />
        </v-col>
      </v-row>
      <!--Description and MeasureUnit -->
      <DescriptionDetails
        :description="sessionKpi.description"
        :unitMeasure="sessionKpi.definition?.unitMeasure || ''"
        :showUnitMeasure="showUnitMeasure"
      />
      <!-- Formula & Geolocation -->
      <v-row class="mt-4">
        <!-- formula -->
        <v-col v-if="!isManualKpi" :cols="locationSelected ? 8 : 12">
          <MathfieldDetails
            :formula="sessionKpi?.formula"
            :isFormulaValid="validFormula"
            :scheduleValue="sessionKpi.formula?.job?.schedule?.value"
            :scheduleTime="sessionKpi.formula?.job?.schedule?.time"
          />
        </v-col>
        <!-- geolocation -->
        <v-col
          v-if="locationSelected"
          :cols="isManualKpi ? 12 : locationSelected ? 4 : 0"
        >
          <GeolocationDetails
            :geolocation="sessionKpi?.definition?.geolocation"
            :min-height="isManualKpi ? '230px' : '115px'"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { fetchChartData } from "../composables/fetchChartData";
import StatusAutomatic from "./StatusAutomatic.vue";
import StatusManual from "./StatusManual.vue";
import ChartDetails from "./ChartDetails.vue";
import DescriptionDetails from "./DescriptionDetails.vue";
import MathfieldDetails from "@/components/MathfieldDetails.vue";
import GeolocationDetails from "@/components/GeolocationDetails.vue";
import BadgesDetails from "./BadgesDetails.vue";

export default {
  components: {
    BadgesDetails,
    MathfieldDetails,
    ChartDetails,
    DescriptionDetails,
    StatusAutomatic,
    StatusManual,
    GeolocationDetails,
  },
  props: {
    apiUrl: {
      type: String,
      required: true,
    },
    realm: {
      type: String,
      required: true,
    },
    kpi: { type: Object },
    validFormula: { type: Boolean },
  },
  emits: ["writeCronJobStatus", "toggleOverlay"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const sessionKpi = JSON.parse(JSON.stringify(props.kpi));
    const kpiStatus = ref(props.kpi?.formula?.job?.status.toUpperCase());
    const chartData = ref({});
    const chartType = ref(sessionKpi?.dashboard?.chart.type);
    const mockupAlert = reactive({ value: false });
    const loading = ref(false);
    
    const locationSelected = computed(() => {
      return (
        sessionKpi.definition.geolocation.latitude !== "" &&
        sessionKpi.definition.geolocation.longitude !== ""
      );
    });

    // Determine if KPI stored in sessionStorage is Manual. The stored object key is `kpi`.
    const isManualKpi = computed(() => {
      try {
        const raw = sessionStorage.getItem("kpi");
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.kpiType) {
              return String(parsed.kpiType).toLowerCase() === "manual";
            }
          } catch (e) {
            // If stored as simple string
            if (String(raw).toLowerCase() === "manual") return true;
          }
        }
      } catch (e) {
        console.error(
          "There is a problem with KPI object in Session Storage: ",
          e
        );
      }
      return String(sessionKpi.kpiType || "").toLowerCase() === "manual";
    });
    // Determine if KPI values are of type QUALITATIVE
    const isQualitative = computed(() => {
      try {
        const def = sessionKpi.definition || {};
        const type = def.valueType || "";
        return String(type).toLowerCase() === "qualitative";
      } catch (e) {
        return false;
      }
    });

    // Determine if the chart is horizontal (for bar charts only)
    const chartHorizontal = computed(() => {
      return sessionKpi?.dashboard?.chart?.horizontal || false;
    });

    // Show Unit Measure unless KPI is Manual AND Qualitative
    const showUnitMeasure = computed(() => {
      return !(isManualKpi.value && isQualitative.value);
    });
    const statistics = reactive({
      min: 0,
      max: 0,
      avg: 0,
    });
    watch(chartData, (newData) => {
      if (newData && newData.statistics) {
        statistics.value = newData.statistics;
      }
    });
    onMounted(async () => {
      loading.value = true;
      try {
        let obj = await fetchChartData(props.apiUrl, props.realm);
        chartData.value = obj.chartData || {};
        mockupAlert.value = obj.mockupAlert;
        Object.assign(statistics, obj.kpiHistoricalData.value.data.statistics);
      } catch (error) {
        console.error("Errore durante il fetch dei dati:", error);
      } finally {
        loading.value = false;
        emit("toggleOverlay", loading.value);
      }
    });

    function toggleCronJob(switchValue) {
      emit("writeCronJobStatus", switchValue);
    }

    return {
      sessionKpi,
      kpiStatus,
      chartData,
      statistics,
      chartType,
      t,
      mockupAlert,
      locationSelected,
      isManualKpi,
      isQualitative,
      chartHorizontal,
      showUnitMeasure,
      toggleCronJob,
    };
  },
};
</script>

<style scoped>
.geolocation-expanded {
  min-height: 480px;
  width: 100%;
}

/* Ensure inner map or content stretches the column */
.geolocation-expanded > * {
  height: 100%;
}
</style>
